import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { colors } from '@guryou/colors';
import { randomProvidersActionByKeywordOrAddress, stateIsLoaded } from '@guryou/redux';
import { Modal, ProviderCard, SkeletonLoader, CategoryTag, ImageWithFallback } from '../../../components/shared';

const BookSection = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const randomProviders = useSelector(state => state.randomProviders);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [isCenterModalOpen, setIsCenterModalOpen] = useState(false);

  const centers = randomProviders?.data?.response?.centers?.slice(0, 6);
  const professionals = randomProviders?.data?.response?.professionals?.slice(0, 4);

  useEffect(() => {
    dispatch(randomProvidersActionByKeywordOrAddress());
  }, [dispatch]);

  const openModal = professional => {
    setSelectedProfessional(professional);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedProfessional(null);
    setIsModalOpen(false);
  };

  const openCenterModal = center => {
    setSelectedCenter(center);
    setIsCenterModalOpen(true);
  };

  const closeCenterModal = () => {
    setSelectedCenter(null);
    setIsCenterModalOpen(false);
  };

  // Show loading if data is not loaded or if we don't have data yet
  if (!stateIsLoaded || !randomProviders?.data?.response) {
    return (
      <section className="py-16 lg:py-24" style={{ backgroundColor: colors.white }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Loading Header */}
          <SkeletonLoader type="header" />

          {/* Loading Centers Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            <SkeletonLoader type="card" count={6} />
          </div>

          {/* Loading Professionals Section */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl" />
            <div className="relative py-16 px-8">
              <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center p-2 mb-6 rounded-full" style={{ backgroundColor: 'var(--feel-primary)' }}>
                  <div className="w-6 h-6 rounded bg-white animate-pulse"></div>
                </div>
                <div className="h-12 bg-gray-300 rounded-lg mx-auto mb-6 w-80 animate-pulse"></div>
                <div className="h-6 bg-gray-300 rounded-lg mx-auto w-2/3 animate-pulse"></div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {[...Array(4)].map((_, index) => (
                  <div key={index} className="text-center">
                    <div className="relative mb-6">
                      <div className="w-32 h-32 mx-auto rounded-full bg-gray-300 ring-4 ring-white shadow-lg animate-pulse"></div>
                      <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                        <div className="w-8 h-8 rounded-full bg-gray-300 animate-pulse"></div>
                      </div>
                    </div>
                    <div className="h-6 bg-gray-300 rounded-lg mb-3 w-3/4 mx-auto animate-pulse"></div>
                    <div className="flex flex-wrap justify-center gap-2 mb-4">
                      <div className="h-5 bg-gray-300 rounded-full w-16 animate-pulse"></div>
                      <div className="h-5 bg-gray-300 rounded-full w-20 animate-pulse"></div>
                    </div>
                    <div className="h-8 bg-gray-300 rounded-full w-24 mx-auto animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24" style={{ backgroundColor: colors.white }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center p-2 mb-6 rounded-full" style={{ backgroundColor: colors.carouselPink }}>
            <svg className="w-6 h-6" style={{ color: 'var(--feel-primary)' }} fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: colors.mineShaft }}>
            {t('BOOK_SECTION.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">{t('BOOK_SECTION.description')}</p>
        </div>

        {/* Centers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {centers?.map(card => (
            <ProviderCard key={card.id} provider={card} type="center" onViewProfile={openCenterModal} />
          ))}
        </div>

        {/* Professionals Section */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl" />
          <div className="relative py-16 px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center p-2 mb-6 rounded-full" style={{ backgroundColor: 'var(--feel-primary)' }}>
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                </svg>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: colors.mineShaft }}>
                {t('BOOK_SECTION.expertProfessionals')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">{t('BOOK_SECTION.professionalsDescription')}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {professionals?.map(card => (
                <ProviderCard key={card.id} provider={card} type="professional" onViewProfile={openModal} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Professional Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal} title={selectedProfessional?.name}>
        {selectedProfessional && (
          <>
            {/* Modal Header */}
            <div className="flex items-center space-x-4 p-6 border-b border-gray-200">
              <div className="w-20 h-20 rounded-full overflow-hidden ring-4 ring-white shadow-lg" style={{ ringColor: 'var(--feel-primary)' }}>
                <ImageWithFallback
                  src={selectedProfessional.images?.[0]?.medium}
                  alt={selectedProfessional.name}
                  className="w-full h-full object-cover"
                  fallbackIcon={
                    <svg className="w-8 h-8 opacity-50" style={{ color: colors.mineShaft }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  }
                />
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap gap-2">
                  {selectedProfessional?.categories?.slice(0, 3).map((cat, index) => (
                    <CategoryTag key={index} name={cat.name} size="sm" variant="secondary" />
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {selectedProfessional.description && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-3" style={{ color: colors.mineShaft }}>
                    {t('BOOK_SECTION.about')}
                  </h4>
                  <p className="text-gray-600 leading-relaxed">{selectedProfessional.description}</p>
                </div>
              )}

              {selectedProfessional?.categories && selectedProfessional.categories.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-3" style={{ color: colors.mineShaft }}>
                    {t('BOOK_SECTION.specializations')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProfessional.categories.map((cat, index) => (
                      <CategoryTag key={index} name={cat.name} size="md" variant="default" />
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3" style={{ color: colors.mineShaft }}>
                  {t('BOOK_SECTION.contactInformation')}
                </h4>
                <div className="space-y-2">
                  {selectedProfessional.email && (
                    <div className="flex items-center space-x-3">
                      <svg className="w-5 h-5" style={{ color: 'var(--feel-primary)' }} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                      <span className="text-gray-600">{selectedProfessional.email}</span>
                    </div>
                  )}
                  {selectedProfessional.phone && (
                    <div className="flex items-center space-x-3">
                      <svg className="w-5 h-5" style={{ color: 'var(--feel-primary)' }} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                      <span className="text-gray-600">{selectedProfessional.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                <button
                  className={`flex-1 px-6 py-3 rounded-full font-semibold text-white transition-all duration-200 hover:scale-105 bg-[var(--feel-primary)] hover:bg-[var(--feel-primary-dark)]`}
                >
                  {t('BOOK_SECTION.bookAppointment')}
                </button>
                <button
                  className={`flex-1 px-6 py-3 rounded-full font-semibold border-2 transition-all duration-200 hover:scale-105 border-[var(--feel-primary)] text-[var(--feel-primary)] bg-transparent hover:bg-[var(--feel-primary)] hover:text-white`}
                >
                  {t('BOOK_SECTION.sendMessage')}
                </button>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* Center Modal */}
      <Modal isOpen={isCenterModalOpen} onClose={closeCenterModal} title={selectedCenter?.name}>
        {selectedCenter && (
          <>
            {/* Modal Header */}
            <div className="flex items-center space-x-4 p-6 border-b border-gray-200">
              <div className="w-20 h-20 rounded-lg overflow-hidden ring-4 ring-white shadow-lg" style={{ ringColor: 'var(--feel-primary)' }}>
                <ImageWithFallback
                  src={selectedCenter.images?.[0]?.medium}
                  alt={selectedCenter.name}
                  className="w-full h-full object-cover"
                  fallbackIcon={
                    <svg className="w-8 h-8 opacity-50" style={{ color: colors.mineShaft }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  }
                />
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap gap-2">
                  {selectedCenter?.categories?.slice(0, 3).map((cat, index) => (
                    <CategoryTag key={index} name={cat.name} size="sm" variant="default" />
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {selectedCenter.description && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-3" style={{ color: colors.mineShaft }}>
                    {t('BOOK_SECTION.about')}
                  </h4>
                  <p className="text-gray-600 leading-relaxed">{selectedCenter.description}</p>
                </div>
              )}

              {selectedCenter?.categories && selectedCenter.categories.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-3" style={{ color: colors.mineShaft }}>
                    {t('BOOK_SECTION.servicesAndAmenities')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedCenter.categories.map((cat, index) => (
                      <CategoryTag key={index} name={cat.name} size="md" variant="default" />
                    ))}
                  </div>
                </div>
              )}

              {/* Location Information */}
              {selectedCenter.location && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-3" style={{ color: colors.mineShaft }}>
                    {t('BOOK_SECTION.location')}
                  </h4>
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 mt-1" style={{ color: 'var(--feel-primary)' }} fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div>
                      <p className="text-gray-600 font-medium">{selectedCenter.location.address}</p>
                      {selectedCenter.location.city && <p className="text-gray-500 text-sm">{selectedCenter.location.city}</p>}
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3" style={{ color: colors.mineShaft }}>
                  {t('BOOK_SECTION.contactInformation')}
                </h4>
                <div className="space-y-2">
                  {selectedCenter.email && (
                    <div className="flex items-center space-x-3">
                      <svg className="w-5 h-5" style={{ color: 'var(--feel-primary)' }} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                      <span className="text-gray-600">{selectedCenter.email}</span>
                    </div>
                  )}
                  {selectedCenter.phone && (
                    <div className="flex items-center space-x-3">
                      <svg className="w-5 h-5" style={{ color: 'var(--feel-primary)' }} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                      <span className="text-gray-600">{selectedCenter.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                <button
                  className={`flex-1 px-6 py-3 rounded-full font-semibold text-white transition-all duration-200 hover:scale-105 bg-[var(--feel-primary)] hover:bg-[var(--feel-primary-dark)]`}
                >
                  {t('BOOK_SECTION.bookAppointment')}
                </button>
                <button
                  className={`flex-1 px-6 py-3 rounded-full font-semibold border-2 transition-all duration-200 hover:scale-105 border-[var(--feel-primary)] text-[var(--feel-primary)] bg-transparent hover:bg-[var(--feel-primary)] hover:text-white`}
                >
                  {t('BOOK_SECTION.sendMessage')}
                </button>
              </div>
            </div>
          </>
        )}
      </Modal>
    </section>
  );
};

export default BookSection;
